import { Inject, Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import {
  NavbarConfigService,
  AuthService,
  GungFlowService,
  AssortmentService,
  SelectedCustomerService,
  NavGroup,
  NavLink,
  CartService,
  GungModalService,
  NavAction,
  CustomerService,
  Customer,
  NewsActionConfigService,
  BackendInterceptor,
  DocumentsService,
  PriceConfigService,
  Document,
  BaseViewConfigService,
  GungCompanyService,
  NavigationConfig,
  LocationConfigService,
  MetadataService,
  SelectedSupplierService,
  BackendFeatureService,
  GungNavbarService,
  GungFeatureMap
} from 'gung-standard';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { filter, first, mergeMap, map } from 'rxjs';
import { Location } from '@angular/common';
import { GungUser as User } from 'gung-standard';
import { environment as env } from './../../environments/environment';
import { TyloModalService } from './tylo-modal.service';
import { HttpClient } from '@angular/common/http';
import { TyloAssortmentServiceService } from './tylo-assortment-service.service';
import { inject } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TyloNavbarConfigService extends NavbarConfigService {
  public enableSwitchPrice = false;
  enableDigitalAssets = true;
  enableDocumentUserGroups = true;

  isTylo = env.projectName === 'tylo';
  isHelo = env.projectName === 'helo';

  protected gungModalService: TyloModalService = inject(TyloModalService);
  protected assortmentService: TyloAssortmentServiceService = inject(TyloAssortmentServiceService);
  isActiveForClaimsWarranty = false;

  constructor(
    @Optional()
    @Inject('environment')
    protected environment: NavigationConfig
  ) {
    super(environment);
  }

  public getLeftMenus(): Observable<NavGroup[]> {
    const baseMenu: NavGroup[] = [];
    return forkJoin([
      this.authService.getCurrentUser().pipe(first()),
      this.selectedCustomerService.getSelectedCustomer().pipe(first())
    ]).pipe(
      first(),
      mergeMap(([user, selectedCustomer]) =>
        forkJoin([
          this.isHelo
            ? this.assortmentService
                .getAssortmentRecursiveCustomerFilteredNoProducts(user.assortment, selectedCustomer.id)
                .pipe(first())
            : this.assortmentService.getAssortmentRecursive(user.assortment).pipe(first()),
          this.userRoles().pipe(first())
        ])
      ),
      map(([assortment, userRoles]) => {
        if (userRoles.indexOf('DIGITALASSETS') > -1) {
          const digitalAssetsmMenu = {
            heading: 'DIGITAL_ASSETS',
            link: '/digital-assets',
            links: undefined
          } as NavGroup;
          baseMenu.unshift(digitalAssetsmMenu);
        } else {
          const productsMenu = {
            heading: 'PRODUCTS',
            link: this.getProductListBaseRoute(),
            links: undefined
          } as NavGroup;
          if (assortment && assortment.children) {
            productsMenu.links = assortment.children.map(
              childAssortment =>
                ({
                  heading: childAssortment.name,
                  link: `/categories/${childAssortment.id}`,
                  links: childAssortment.children.map(
                    subChildAssortment =>
                      ({
                        heading: subChildAssortment.name,
                        link: `/categories/${subChildAssortment.id}`
                      }) as NavLink
                  )
                }) as NavGroup
            );
          }
          baseMenu.unshift(productsMenu);
        }
        return baseMenu;
      })
    );
  }

  userGroups(): Observable<{ [s: string]: any }> {
    return this.authService.getCurrentUser().pipe(
      filter(user => !!user),
      map(user => user.activeGroups)
    );
  }

  public getMainMenu(): Observable<NavGroup> {
    return this.userRoles().pipe(
      mergeMap(roles =>
        forkJoin([
          of(roles),
          this.selectedCustomerService.getSelectedCustomer().pipe(
            filter(customer => !!customer),
            first()
          ),
          this.customerService.getCustomers().pipe(
            filter(customer => !!customer),
            first()
          ),
          this.newsActionConfigService.getIsEnabledShowUserNewsInPage().pipe(first()),
          this.enableDocumentUserGroups ? this.getUserDocuments().pipe(first()) : of([]),
          this.userGroups().pipe(first()),
          this.authService.getCurrentUser().pipe(first())
        ])
      ),
      map(([roles, customer, customers, isEnabledShowUserNewsInPage, documents, groups, user]) => {
        this.isEnabledShowUserNewsInPage = isEnabledShowUserNewsInPage;
        this.documents = documents;
        const mainMenu: NavGroup = {
          heading: '',
          links: [
            {
              link: undefined,
              heading: 'LOGOUT',
              // icon: 'fa-file-invoice-dollar',
              action: () => {
                this.logout();
              }
            }
          ]
        };
        if (roles.indexOf('SALES') > -1 || roles.indexOf('ADMIN') > -1) {
          if (roles.indexOf('ADMIN') > -1) {
            mainMenu.links.unshift(...this.getAdminMenu(roles.indexOf('ACTUATOR') > -1));
          }
          mainMenu.links.unshift(...this.getSalesMenu(roles.indexOf('ACTUATOR') > -1, roles, undefined, undefined, user));
        }
        if (!(roles.indexOf('DIGITALASSETS') > -1)) {
          const userMenu = this.getUserMenu(
            customer,
            roles.indexOf('SALES') > -1,
            customers.length,
            roles.indexOf('ACTUATOR') > -1,
            undefined,
            user
          );

          mainMenu.links.unshift(...userMenu);
        }
        if (
          this.priceConfigService.enableHidePrice &&
          !groups.NOPRICE &&
          !groups.VIEW &&
          roles.indexOf('DIGITALASSETS') === -1
        ) {
          const hidePriceSwitch: NavGroup = {
            link: undefined,
            heading: 'HIDE_PRICE',
            action: (inputElement: HTMLInputElement) => {
              this.priceConfigService.setHidePrice(inputElement.checked);
            }
          };
          mainMenu.links.push(hidePriceSwitch);
        }

        if (this.isTylo && roles.indexOf('DIGITALASSETS') === -1) {
          mainMenu.links.push({
            link: undefined,
            heading: 'SHOW_B2C_PRICES',
            action: () => {}
          });
        }

        if (roles.includes('ACTUATOR')) {
          const roleLinks: NavGroup[] = [];
          user.roles.forEach(role => {
            if (role !== 'ACTUATOR') {
              roleLinks.push({
                heading: role,
                link: 'TOGGLE',
                action: (_inputElement: HTMLInputElement) => this.authService.toggleMockRole(role),
                icon: roles.includes(role) ? 'fa-toggle-on' : 'fa-toggle-off'
              });
            }
          });

          const mockRoleMode = this.authService.mockRolesModeEnabled();

          mainMenu.links.push({
            heading: mockRoleMode ? 'Disable mock roles' : 'Enable mock roles',
            links: mockRoleMode ? roleLinks : [],
            action: () => this.authService.toggleUseMockRoles()
          });
        }

        return mainMenu;
      })
    );
  }

  protected getUserMenu(
    customer: Customer,
    isSales?: boolean,
    customers?: number,
    isActuator?: boolean,
    features?: GungFeatureMap,
    user?: User
  ): (NavLink | NavAction | NavGroup)[] {
    const menuLinks = super.getUserMenu(customer, isSales, customers, isActuator) as NavGroup[];

    // All non-role-dependent modifications happen here synchronously
    menuLinks[0].links.splice(
      menuLinks[0].links.findIndex(d => d.heading === 'PREVIOUS_ORDERS'),
      0,
      {
        link: '/delivery-planning',
        heading: 'DELIVERY_PLANNING'
      }
    );

    if (menuLinks[0].links.findIndex(d => d.heading === 'SAVED_QUOTES') === -1) {
      menuLinks[0].links.splice(menuLinks[0].links.findIndex(d => d.heading === 'DELIVERY_PLANNING') + 1, 0, {
        link: '/saved-quotes',
        heading: 'SAVED_QUOTES'
      });
    }

    let favouritesIndex = (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'FAVOURITES');
    menuLinks[0].links.splice(++favouritesIndex, 0, { link: '/view-documents', heading: 'DOCUMENTS' });

    if (this.isTylo) {
      menuLinks[0].links.splice(++favouritesIndex, 0, {
        heading: '3D_CONFIGURATOR',
        action: () => {
          window.open('https://3dconfigurator.tylo.com/', '_blank');
        }
      });
    } else {
      menuLinks[0].links.push({ link: '/warranty-form', heading: 'CLAIM_FORM' });
    }

    if (this.enableDigitalAssets) {
      const digitalAssetsIndex = (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'DIGITAL_ASSETS');
      if (digitalAssetsIndex > -1) {
        const [digitalAssets] = (menuLinks[0] as NavGroup).links.splice(digitalAssetsIndex, 1);
        (menuLinks[0] as NavGroup).links.push(digitalAssets);
      }
    }

    if (this.isTylo) {
      (menuLinks[0] as NavGroup).links.push({
        heading: 'CUSTOMER_B2C_PRICE_MARKUP',
        action: () => {
          this.gungModalService.openTyloCustomerB2cPriceMarkupModal();
        }
      });

      (menuLinks[0] as NavGroup).links.push({
        heading: 'CONTACT_US',
        link: '/page/contact-us'
      });
    }

    // Add RETURN_WARRANTY at the end if user has ACTIVE_FOR_CLAIMS_WARRANTY role
    if (!isSales && user?.roles?.includes('ACTIVE_FOR_CLAIMS_WARRANTY')) {
      (menuLinks[0] as NavGroup).links.push({
        heading: 'RETURN_WARRANTY',
        action: () => {
          this.router.navigate(['/return-warranty']);
        }
      });
    }

    const hasNoPriceGroup: boolean = !!user?.activeGroups?.['NOPRICE'];
    // Remove specific links if user has NOPRICE group
    if (hasNoPriceGroup) {
      (menuLinks[0] as NavGroup).links = (menuLinks[0] as NavGroup).links.filter(
        item => !('link' in item) || (item.link !== '/orders' && item.link !== '/carts' && item.link !== '/saved-quotes')
      );
    }

    return menuLinks;
  }

  protected getUserDocuments(nocache?: boolean): Observable<Document[]> {
    return forkJoin({
      user: this.authService.getCurrentUser().pipe(first()),
      documents: this.documentsService.getDocuments(nocache).pipe(first()),
      customer: this.selectedCustomerService.getSelectedCustomer().pipe(
        filter(customer => !!customer),
        first()
      )
    }).pipe(
      first(),
      map(({ user, documents, customer }) => {
        return documents.filter(
          doc =>
            doc.metaData &&
            doc.metaData.inMenu &&
            doc.metaData.inMenu === 'true' &&
            this.showDocumentExt(doc, user, customer)
        );
      })
    );
  }

  showDocumentExt(document, user: User, customer: Customer): boolean {
    let result = false;
    if (document && document.metaData && document.metaData.userGroups) {
      const currentUserKeys = Object.keys(user.activeGroups).filter(userGroup => user.activeGroups[userGroup] === true);
      if (document.metaData && document.metaData.userGroups && typeof document.metaData.userGroups === 'string') {
        document.metaData.userGroups = JSON.parse(document.metaData.userGroups);
      }
      const documentKeys = Object.keys(document.metaData.userGroups).find(
        userGroup => document.metaData.userGroups[userGroup] === true
      );
      const groupKey = Object.keys(document.metaData.userGroups).find(
        userGroup => document.metaData.userGroups[userGroup] === true && currentUserKeys.some(key => key === userGroup)
      );
      if (groupKey) {
        result = true;
      } else {
        result = false;
      }

      if (document.metaData.includedAssortments) {
        const assortmentList: string[] = document.metaData.includedAssortments.split(',');
        if (!assortmentList.includes(user.assortment)) {
          return false;
        }
      }
      if (!customer) {
        return false;
      }
      if (document.metaData.includedCustomers) {
        const customerList: string[] = document.metaData.includedCustomers.split(',');
        if (!customerList.includes(customer.id)) {
          return false;
        }
      }
      if (document.metaData.excludedCustomers) {
        const customerList: string[] = document.metaData.excludedCustomers.split(',');
        if (customerList.includes(customer.id)) {
          return false;
        }
      }
    }
    return result;
  }

  public showCart(): Observable<boolean> {
    return this.authService.getCurrentUser().pipe(
      filter(user => !!user),
      first(),
      map(user => !(user.roles.indexOf('ANONYMOUS') > -1 || user.activeGroups.NOPRICE))
    );
  }

  protected getSalesMenu(
    isActuator?: boolean,
    roles?: string[],
    salesCodeErp?: string,
    features?: GungFeatureMap,
    user?: User
  ): (NavLink | NavAction | NavGroup)[] {
    const menuLinks = super.getSalesMenu(isActuator, roles, salesCodeErp, features);

    // Add or remove RETURN_WARRANTY link based on user role
    if (user?.roles?.includes('ACTIVE_FOR_CLAIMS_WARRANTY')) {
      // Remove existing link-based RETURN_WARRANTY and replace with action-based
      menuLinks.forEach(menuGroup => {
        if ('links' in menuGroup && Array.isArray(menuGroup.links)) {
          const returnWarrantyIndex = menuGroup.links.findIndex(
            link => ('link' in link && link.link === '/return-warranty') || 
                    (link.heading === 'RETURN_WARRANTY')
          );
          if (returnWarrantyIndex > -1) {
            // Replace in the same position
            menuGroup.links.splice(returnWarrantyIndex, 1, {
              heading: 'RETURN_WARRANTY',
              action: () => {
                this.router.navigate(['/return-warranty']);
              }
            });
          } else {
            // Add at the end if it didn't exist
            menuGroup.links.push({
              heading: 'RETURN_WARRANTY',
              action: () => {
                this.router.navigate(['/return-warranty']);
              }
            });
          }
        }
      });
    } else {
      // Remove RETURN_WARRANTY link if user doesn't have the role
      menuLinks.forEach(menuGroup => {
        if ('links' in menuGroup && Array.isArray(menuGroup.links)) {
          const returnWarrantyIndex = menuGroup.links.findIndex(
            link => 'link' in link && link.link === '/return-warranty'
          );
          if (returnWarrantyIndex > -1) {
            menuGroup.links.splice(returnWarrantyIndex, 1);
          }
        }
      });
    }

    return menuLinks;
  }

  protected getAdminMenu(isActuator?: boolean): (NavLink | NavAction | NavGroup)[] {
    const menuLinks = super.getAdminMenu(isActuator);

    (menuLinks[1] as NavGroup).links.push({ link: '/categories', heading: 'HOME_ASSORTMENT' });

    return menuLinks;
  }
}
